<template>
  <div class="w-full h-full relative bg-gradient-to-r from-white to-blue-200 select-none">
    <div ref="renderer" class="w-full h-full relative" />
    <div v-if="!isLoaded" class="absolute inset-0 bg-white/20 backdrop-blur-[1px]" @click.stop.prevent />
    <div class="absolute top-2 left-2 z-10 flex gap-x-2">
      <button v-if="currentZ !== null" class="px-2 py-1 bg-white border border-primary-300 text-sm text-primary-500 rounded" @click="setDefaultView">
        Default View
      </button>

      <template v-else>
        <button
          class="px-2 py-1 bg-white border text-sm rounded"
          :class="isAnythingSelected ? 'border-primary-300 text-primary-500' : 'border-gray-200 text-gray-400'"
          :disabled="!isAnythingSelected"
          @click="isolateSelected"
        >
          {{ isolationLevel === 2 ? 'Unhide' : isolationLevel === 1 ? 'Hide' : 'Isolate' }}
        </button>
        <button class="px-2 py-1 bg-white border border-primary-300 text-sm text-primary-500 rounded" @click="topView">
          Top View
        </button>
        <select class="pl-2 pr-8 py-1 bg-white border border-primary-300 text-sm text-primary-500 rounded" @change="e => setViewMode(e.target.value)">
          <option v-for="mode in viewModes" :key="mode" :value="mode">{{ mode }}</option>
        </select>
      </template>
    </div>
  </div>
</template>

<script setup>
import {
  useViewer,
  disposeViewer,
  findNodesById,
  ViewerEvent,
  selectObjects,
  clearSelection,
  isolateObjects,
  hideObjects,
  zoomToObjects,
  resetFilters,
  ViewMode,
  setViewMode,
  setTopView,
  setDefaultView,
  currentZ
} from '../composables/useViewer';
import { ref, computed, watchEffect, onMounted, onUnmounted, nextTick } from 'vue';
import { useVModel, useResizeObserver, useDebounceFn } from '@vueuse/core';
import { roomIds } from '../store/PanelList';

const ignoreCategories = [
  'Areas',
  'Rooms',
  'Topography'
];

const viewModes = Object.keys(ViewMode).filter(key => Number.isNaN(Number(key)));

defineExpose({ selectObject, infoObject, zoomToObjects, isolateObjects, setTopView });

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits([ 'progress', 'loaded', 'info', 'update:modelValue' ]);

const renderer = ref(null);
const isLoaded = ref(false);

const multiSelectList = useVModel(props, 'modelValue', emit);
const isAnythingSelected = computed(() => multiSelectList.value?.length > 0);

const isolationLevel = ref(0);

watchEffect(() => {
  if (!isLoaded.value) {
    return;
  }

  reset();

  if (multiSelectList.value.length === 0) {
    return;
  }

  const ids = multiSelectList.value.map(val => val.id);
  selectObjects(ids);
});

let viewer;

onMounted(async () => {
  viewer = useViewer(renderer.value, progress => emit('progress', progress));

  useResizeObserver(renderer, useDebounceFn(() => {
    viewer?.resize();
  }, 200));

  viewer.on(ViewerEvent.LoadComplete, () => {
    console.log('LoadComplete!');
    emit('loaded');
    viewer.resize();
    reset();
    isLoaded.value = true;
  });

  viewer.on(ViewerEvent.ObjectClicked, async selectionInfo => {
    console.log('ObjectClicked', selectionInfo);
    emit('info', null);

    clearSelection();

    if (isolationLevel.value) {
      resetFilters();
      hideObjects(roomIds.value);
      isolationLevel.value = 0;
    }

    if (!selectionInfo) {
      clearSelection();
      multiSelectList.value = [];
      return;
    }

    const hits = filterHits(selectionInfo);
    if (hits.length === 0) {
      multiSelectList.value = [];
      return;
    }

    const firstRaw = hits[0].node.model?.raw || hits[0].node.raw;

    if (!selectionInfo.multiple || multiSelectList.value.length === 0) {
      multiSelectList.value = [ firstRaw ];
      return;
    }

    const ids = multiSelectList.value.map(val => val.id);
    if (ids.includes(firstRaw.id)) {
      multiSelectList.value = multiSelectList.value.filter(val => val.id !== firstRaw.id);
    } else {
      multiSelectList.value.push(firstRaw);
    }
  });

  viewer.on(ViewerEvent.ObjectDoubleClicked, async selectionInfo => {
    if (!selectionInfo) {
      return;
    }

    const hits = filterHits(selectionInfo);
    if (hits.length === 0) {
      return;
    }

    const firstRaw = hits[0].node.model?.raw || hits[0].node.raw;
    nextTick(() => zoomToObjects([ firstRaw.id ]));
    emit('info', firstRaw);
  });

  console.log('Loading started!');
});

onUnmounted(() => {
  disposeViewer();
});

function filterHits(selectionInfo) {
  if (!selectionInfo) {
    return [];
  }
  return selectionInfo.hits.filter(val => {
    let zFilter = true;
    if (currentZ.value !== null) {
      zFilter = val.point.z < currentZ.value;
    }
    const raw = val.node.model?.raw || val.node.raw;
    return zFilter && raw?.category && !ignoreCategories.includes(raw.category);
  });
}

function reset() {
  try {
    resetFilters();
    clearSelection();
    hideObjects(roomIds.value);
    isolationLevel.value = 0;

  } catch (e) {
    console.log('Error resetting viewer', e);
  }
}

function selectObject(id) {
  const nodes = findNodesById(id);
  if (!nodes.length) return;

  multiSelectList.value = nodes.map(node => node.model?.raw || node.raw).filter(Boolean);

  reset();
  selectObjects([ id ]);
  zoomToObjects([ id ]);
}

function infoObject(id) {
  const nodes = findNodesById(id);
  if (!nodes.length) return;

  const raw = nodes[0].model?.raw || nodes[0].raw;
  if (raw) {
    emit('info', raw);
  }
}

function isolateSelected() {
  if (isolationLevel.value === 2) {
    reset();
    return;
  }

  if (multiSelectList.value.length === 0) {
    return;
  }

  const ids = multiSelectList.value.map(val => val.id);
  isolateObjects(ids, undefined, true, isolationLevel.value === 0);
  isolationLevel.value++;
}

function topView() {
  setTopView();
  if (multiSelectList.value.length === 0) {
    return;
  }

  const ids = multiSelectList.value.map(val => val.id);
  zoomToObjects(ids);
}
</script>
